import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';
import { useModal } from 'Store/modal';
import patchSchedule from 'Api/schedule/patchSchedule';

import { useScheduleState } from 'View/schedule/state';

import upsertSchedulePayload from './model/request/upsertSchedulePayload';

const usePatchSchedule = ({ resetFormData }) => {
  const { t } = useTranslation('schedule');
  const queryClient = useQueryClient();
  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();
  const { onOpenModal, onResetModal } = useModal();

  const { updateState, state } = useScheduleState();
  const { selectedEvent } = state;

  return useMutation({
    mutationFn: async ({ id, formData }) => {
      onShowLoader({ variant: 'loader-primary' });

      const payload = upsertSchedulePayload(formData);

      return await patchSchedule({ id, payload });
    },
    onSuccess: () => {
      onOpenToast({
        status: 'success',
        description: t('patchScheduleSuccess'),
        duration: 5,
        alwaysShow: false,
      });

      resetFormData();

      updateState({ isOpenFormUpsert: false, initialSelectedSchedule: {}, isEditFormUpsert: false, isDialogOpen: false, isDrawerOpen: false });

      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-details', selectedEvent] });
    },
    onError: (error) => {
      const { response } = error;
      if (response.status === 400) {
        onOpenModal({
          isOpen: true,
          type: 'error',
          title: t('patchScheduleFailedTitle'),
          contentTitle: t('patchScheduleFailedConflictTitle'),
          contentDescription: t('patchScheduleFailedConflictDesc'),
          showPrimaryButton: true,
          primaryButtonText: t('patchScheduleFailedConflictPrimary'),
          showSecondaryButton: true,
          secondaryButtonText: t('patchScheduleFailedConflictSecondary'),
          onClickSecondaryButton: () => {
            resetFormData();
            updateState({ isOpenFormUpsert: false, initialSelectedSchedule: {}, isEditFormUpsert: false });
            onResetModal();
          },
          onClickPrimaryButton: () => {
            onResetModal();
          },
        });
      } else {
        onOpenToast({
          status: 'error',
          description: t('patchScheduleFailedServerDesc'),
          duration: 5,
          alwaysShow: false,
        });
      }
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePatchSchedule;
